<script setup lang="ts">
import { computed, ref } from 'vue'

import { useAcquisitionCohort } from '../../composables/useAcquisitionCohort'

import CohortGrid from './CohortGrid.vue'
import CustomCohortModal from './CustomCohortModal.vue'

import { ReportChannelTabs, ReportSectionTitle } from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import {
  Button,
  Icon,
  SegmentedControl,
  Select,
  type SelectOption,
  type UiAsyncState,
} from '@/shared/ui'

// v3 `AppAcquisitionCohortArea.vue`: its own "Acquisition Cohort" section with the platform rail
// on the left, then Metric · Month/Week · Cohort/Custom Cohort · New above the grid. Registered
// as the `18-cohort-retention` section `component` (contract 27 §18), not as a DataVisible view.
const { t } = useI18n()
const cohort = useAcquisitionCohort()
const createOpen = ref(false)

// v3 `metricMatch`: the two seeded wire names carry copy; any other metric the data brings is
// title-cased from its snake_case name (v3 `capitalizeKeyword`).
const METRIC_LABELS: Record<string, () => string> = {
  total_revenue: () => t('commerceAnalytics.appAcquisition.cohort.metrics.revenue'),
  mmp_sessions: () => t('commerceAnalytics.appAcquisition.cohort.metrics.session'),
}

function metricLabel(value: string): string {
  const label = METRIC_LABELS[value]
  if (label) return label()
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const metricOptions = computed<SelectOption<string>[]>(() =>
  cohort.metrics.value.map((value) => ({ value, label: metricLabel(value) })),
)
const frequencyOptions = computed(() =>
  cohort.frequencies.value.map((value) => ({
    value,
    label: t(`commerceAnalytics.appAcquisition.cohort.${value}`),
  })),
)
const sourceOptions = computed<SelectOption<'standard' | 'custom'>[]>(() =>
  cohort.sources.value.map((value) => ({
    value,
    label: t(`commerceAnalytics.appAcquisition.cohort.${value}`),
  })),
)
const state = computed<UiAsyncState>(() =>
  cohort.state.value === 'idle' ? 'empty' : cohort.state.value,
)
// The platform rail is always the cohort's navigation, even with a single platform (design).
const showRail = computed(() => cohort.platforms.value.length > 0)
</script>

<template>
  <div v-if="cohort.sources.value.length" class="cohort-panel flex flex-col">
    <header class="cohort-panel__header flex items-center px-4 mt-4">
      <ReportSectionTitle :title="t('commerceAnalytics.appAcquisition.cohort.title')" />
    </header>

    <div class="cohort-panel__body flex min-w-0 items-stretch">
      <ReportChannelTabs
        v-if="showRail"
        v-model="cohort.platform.value"
        :tabs="cohort.platforms.value"
        v-bind="{ ariaLabel: t('commerceAnalytics.appAcquisition.cohort.platform') }"
      />

      <div class="cohort-panel__content min-w-0 flex-1">
        <div class="cohort-panel__controls flex w-full items-center justify-between gap-3 p-3">
          <div class="w-max">
            <Select
              v-model="cohort.metric.value"
              size="sm"
              :options="metricOptions"
              :label="t('commerceAnalytics.appAcquisition.cohort.metric')"
            />
          </div>
          <SegmentedControl
            v-if="frequencyOptions.length > 1"
            v-model="cohort.frequency.value"
            size="sm"
            variant="filled"
            :options="frequencyOptions"
            :label="t('commerceAnalytics.appAcquisition.cohort.frequency')"
          />
          <SegmentedControl
            v-if="sourceOptions.length > 1"
            v-model="cohort.source.value"
            size="sm"
            variant="filled"
            :options="sourceOptions"
            :label="t('commerceAnalytics.appAcquisition.cohort.source')"
          />
          <Button size="sm" variant="outline" @click="createOpen = true">
            <Icon name="plus" :size="12" />
            {{ t('commerceAnalytics.appAcquisition.cohort.new') }}
          </Button>
        </div>

        <div class="px-5 pb-4">
          <CohortGrid
            :grid="cohort.grid.value"
            :frequency="cohort.frequency.value"
            :state="state"
            @retry="cohort.reload"
          />
        </div>
      </div>
    </div>

    <CustomCohortModal v-model="createOpen" @created="cohort.reload" />
  </div>
</template>
